/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useCallback, useContext, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { useIntl, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';
import type { MutationUpdaterFn } from 'apollo-client';

import { Box } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { PageCommentEditor } from '@confluence/comment-simple/entry-points/PageCommentEditor';
import { type CommentSaveHandler } from '@confluence/comment-simple/entry-points/commentSimpleTypes';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import {
	startEditorSubExperience,
	startEditorExperiences,
} from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { expValEquals } from '@confluence/feature-experiments';
import { AddCommentPromptButton } from '@confluence/comments-common/entry-points/AddCommentPromptButton';
import {
	CommentsType,
	type GraphQLContentStatus,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralCommentMutation';
import { updateApolloCacheParentCallback } from '@confluence/page-comments-queries/entry-points/pageCommentUtils';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState';
import {
	CommentType,
	useCommentsDataActions,
} from '@confluence/comments-data/entry-points/useCommentsData';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { END } from '@confluence/navdex/entry-points/NavdexPointType';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	CommentMode,
	useCommentsPanelActions,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics';
import {
	getTranslatedError,
	isCreateNotPermittedError,
	isViewNotPermittedError,
} from '@confluence/comments-util/entry-points/objectCommentsErrorUtils';
import { fg } from '@confluence/feature-gating';

import type {
	CommentsPanelFooterCreateGeneralCommentMutation as CommentsPanelFooterCreateGeneralCommentMutation$data,
	CommentsPanelFooterCreateGeneralCommentMutationVariables as CommentsPanelFooterCreateGeneralCommentMutation$variables,
} from './__types__/CommentsPanelFooterCreateGeneralCommentMutation';
import { CommentAddPrompt } from './CommentAddPrompt';

const CommentsPanelFooterCreateGeneralCommentMutation = gql`
	mutation CommentsPanelFooterCreateGeneralCommentMutation(
		$pageId: ID!
		$cloudId: ID!
		$input: CreateCommentInput!
		$includeReactions: Boolean!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						isDangling
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactions) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;

const i18n = defineMessages({
	generalCommentEntryAriaLabel: {
		id: 'comments-panel.footer.general-comment.entry.aria-label',
		defaultMessage: 'Add a general comment',
		description: 'The aria label for the general comment entry field in the comments panel footer',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapperContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div.ak-editor-content-area': {
		overflowY: 'scroll',
		height: 95,
	},
});

export const CommentsPanelFooter = () => {
	const { commentMode, isCommentAddPromptFocused } = useCommentsPanelState();
	const { setCommentMode, setIsCommentAddPromptFocused } = useCommentsPanelActions();
	const shouldAutofocusPromptRef = useRef<boolean>(false);

	const [createGeneralCommentFn] = useMutation<
		CommentsPanelFooterCreateGeneralCommentMutation$data,
		CommentsPanelFooterCreateGeneralCommentMutation$variables
	>(CommentsPanelFooterCreateGeneralCommentMutation);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { formatMessage } = useIntl();
	const ariaLabel = formatMessage(i18n.generalCommentEntryAriaLabel);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, edition } = useSessionData();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const pageMode = useGetPageMode();
	const [{ searchSessionId }] = useSearchSessionId();
	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { addNewCommentThreads } = useCommentsDataActions();
	const { updateReadCommentsListState } = useUnreadCommentsActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const handleCommentsPanelSave: CommentSaveHandler = (adfObject, onSuccess) => {
		const pageId = contentId || '';
		const { adf } = adfObject as any;

		startEditorSubExperience({
			name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
		});

		return createGeneralCommentFn({
			// eslint-disable-next-line confluence-feature-gating/inline-usage
			variables: {
				pageId,
				cloudId,
				input: {
					commentBody: {
						value: JSON.stringify(adf),
						representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
					},
					commentSource: Platform.WEB,
					containerId: pageId,
				},
				includeReactions: false,
			},
			update: updateApolloCacheParentCallback(
				'create',
				{
					pageId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
					// Even though we don't pull data using this for the panel, it's required for the footer
					// The panel will work either way, but the footer needs this
					type: [CommentsType.FOOTER],
				},
				isReactionsEnabled,
			) as MutationUpdaterFn<CommentsPanelFooterCreateGeneralCommentMutation$data>,
		})
			.then(({ data }: any) => {
				const commentResult = data.createFooterComment;
				const comment: CommentData = {
					...commentResult,
					replies: [], // not included in the payload
					isUnread: false,
					isOpen: true,
					type: CommentType.GENERAL,
				};

				updateReadCommentsListState((prev) => new Set([...prev, comment.id]));
				addNewCommentThreads({ inline: {}, general: { [comment.id]: comment } });

				onSuccess && onSuccess();
				resetContentChanged();
				setCommentMode(CommentMode.VIEW);

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'created',
						actionSubject: 'comment',
						actionSubjectId: comment.id, // The newly created comment ID
						objectType: contentType,
						objectId: contentId,
						source: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							commentType: AnalyticsCommentType.PAGE,
							source: AnalyticsSource.COMMENTS_PANEL,
							pageMode,
							isReply: false,
							pageType: contentType,
							isLivePage: pageMode === PageMode.LIVE,
							editor: 'v2',
							searchSessionId,
							navdexPointType: END,
							parentCommentId: null, // analytics event schema type expects string or null
						},
					},
				}).fire();

				experienceTracker.succeed({
					name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				});

				setTimeout(() => {
					scrollCommentsPanelToThread({ annotationId: comment.id, scrollOnLoad: true });
				}, 1000);
			})
			.catch((error) => {
				if (fg('confluence_fe_add_page_comment_exp_coverage')) {
					const translatedError = getTranslatedError(error);

					if (
						isCreateNotPermittedError(translatedError) ||
						isViewNotPermittedError(translatedError)
					) {
						// TODO: show an error message to the user for create not permitted
						experienceTracker.abort({
							name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
							reason: translatedError.id ?? '', // reason always has id, this is to make typecheck happy
						});
					} else {
						// TODO: show an error message to the user for the error
						// Mark the experience as failed/aborted and include the error details
						experienceTracker.stopOnError({
							name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
							error,
						});
					}
				}
			});
	};

	const handleCancel = ({ isEditorAborted }: { isEditorAborted: boolean }) => {
		const doCancel = () => {
			if (isEditorAborted) {
				shouldAutofocusPromptRef.current = true;

				experienceTracker.abort({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					reason: 'aborted because comment was cancelled',
				});
			}

			setCommentMode(CommentMode.VIEW);
		};

		if (hasContentChanged) {
			resetContentChanged();
		}
		doCancel();
	};

	const handleOnEditorLoaded = () => {
		experienceTracker.succeed({
			name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
		});
	};

	const openAddCommentEditor = useCallback(() => {
		if (fg('confluence_frontend_comments_a11y_kb')) {
			setIsCommentAddPromptFocused(true);
		}
		startEditorExperiences(
			{
				name: ADD_PAGE_COMMENT_EXPERIENCE,
				attributes: {
					editor: 'FABRIC',
					isSingleExperience: true,
					edition,
					source: 'commentsPanel',
					entryPoint: 'commentsPanel',
				},
			},
			{
				name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
			},
			{
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			},
		);

		setCommentMode(CommentMode.ADD);
	}, [edition, setCommentMode, setIsCommentAddPromptFocused]);

	const addCommentPrompt = expValEquals(
		'cc_comments_standardize_entry_point',
		'isEnabled',
		true,
	) ? (
		<AddCommentPromptButton
			onClick={openAddCommentEditor}
			autofocusRef={shouldAutofocusPromptRef}
			shouldFitContainer
			testId="add-comment-button"
		/>
	) : (
		<CommentAddPrompt
			onClick={openAddCommentEditor}
			// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
			{...(fg('confluence_frontend_comments_a11y_kb')
				? {
						onFocus: () => {
							// Only open the add comment editor if it is not already focused - this is to prevent
							// the prompt from opening immediately after the cancels the editor
							if (!isCommentAddPromptFocused) {
								openAddCommentEditor();
							}
						},
						onBlur: () => setIsCommentAddPromptFocused(false),
						ariaLabel,
					}
				: {
						onFocus: () => {},
						onBlur: () => {},
					})}
			autofocusRef={shouldAutofocusPromptRef}
		/>
	);

	return (
		<FooterWrapperContainer
			style={{
				minHeight: expValEquals('cc_comments_standardize_entry_point', 'isEnabled', true)
					? 'auto'
					: 40,
			}}
		>
			{commentMode === CommentMode.VIEW ? (
				addCommentPrompt
			) : (
				<Box
					data-testId="panel-footer-editor-container"
					// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
					{...(fg('confluence_frontend_comments_a11y_kb') ? { 'aria-label': ariaLabel } : {})}
				>
					<PageCommentEditor
						mode="create"
						saveHandler={handleCommentsPanelSave}
						cancelHandler={handleCancel}
						onEditorLoaded={handleOnEditorLoaded}
						onEditorContentChanged={onChange}
						useNewExperienceAbort
						expandEditor
						isCommentsPanel
					/>
				</Box>
			)}
		</FooterWrapperContainer>
	);
};
